import { Debug } from '../utils/index.js';
import { DEFAULT_CONFIG } from '../constants/index.js';

/**
 * Centralized manager for all cart quantity updates
 */
export class QuantityManager {
    constructor(cartManager) {
        this.cartManager = cartManager;
        this.pendingUpdates = new Map();
        this.isProcessing = false;
        this.lastUpdateTime = null;
        this.updateLock = null;
        this.MINIMUM_UPDATE_INTERVAL = 500;
    }

    /**
     * Single entry point for all quantity updates
     */
    async updateQuantity(variantId, newQuantity, source = 'unknown') {
        try {
            // Prevent rapid updates
            if (this.lastUpdateTime && Date.now() - this.lastUpdateTime < this.MINIMUM_UPDATE_INTERVAL) {
                Debug.log('info', '[QuantityManager] Skipping rapid update request', {
                    variantId,
                    newQuantity,
                    timeSinceLastUpdate: Date.now() - this.lastUpdateTime
                });
                return null;
            }

            // Wait for any pending updates
            if (this.updateLock) {
                await this.updateLock;
            }

            // Create new update lock
            let resolveLock;
            this.updateLock = new Promise(resolve => {
                resolveLock = resolve;
            });

            // Get current state
            const currentCart = await this.cartManager.getCart();
            const currentItem = currentCart.items.find(item => item.variant_id === variantId);

            if (!currentItem) {
                Debug.log('error', '[QuantityManager] Item not found', { variantId });
                return null;
            }

            // Skip if no change
            if (currentItem.quantity === newQuantity) {
                Debug.log('info', '[QuantityManager] Quantity unchanged, skipping update', {
                    variantId,
                    quantity: newQuantity
                });
                return currentCart;
            }

            Debug.log('info', '[QuantityManager] Processing quantity update', {
                variantId,
                oldQuantity: currentItem.quantity,
                newQuantity,
                source
            });

            // Perform the update
            const result = await this.cartManager.changeItemQuantity(variantId, newQuantity);

            // Update timestamp
            this.lastUpdateTime = Date.now();

            return result;

        } catch (error) {
            Debug.log('error', '[QuantityManager] Update failed', {
                variantId,
                newQuantity,
                error
            });
            throw error;
        } finally {
            // Release lock
            if (resolveLock) {
                resolveLock();
            }
            this.updateLock = null;
        }
    }

    /**
     * Handle quantity increment
     */
    async increment(variantId) {
        const currentCart = await this.cartManager.getCart();
        const item = currentCart.items.find(item => item.variant_id === variantId);
        
        if (!item) return null;
        
        return this.updateQuantity(variantId, item.quantity + 1, 'increment');
    }

    /**
     * Handle quantity decrement
     */
    async decrement(variantId) {
        const currentCart = await this.cartManager.getCart();
        const item = currentCart.items.find(item => item.variant_id === variantId);
        
        if (!item) return null;
        if (item.quantity <= 1) return currentCart;
        
        return this.updateQuantity(variantId, item.quantity - 1, 'decrement');
    }
} 